import * as React from 'react';
import * as _ from 'lodash';
import * as Shared from '../shared/shared-types';
import * as SLP from './search-listing-types';
import { AdService } from '../shared/ad-service';
import { ArticleListPager } from '../shared/article-list-pager';
import { CheckboxFilterGroup } from '../shared/checkbox-filter-group';
import { SearchListingHeader } from './search-listing-header';
import { SearchListingFilterStore } from './search-listing-store';
import { SearchArticleList } from './search-article-list';

export class SearchListingPage extends React.Component<SLP.ISearchListingPageProps, SLP.ISearchListingPageState>{

    private _config: SLP.ISearchListingConfig;
    private _store: SearchListingFilterStore;
    private _isMobileDevice: boolean;
    private _initializing: boolean;
    private _adGutter: HTMLElement;
    private _adService: AdService;
    private _fullPageSize: number;
    private _lastActionKey: string;

    constructor(props: SLP.ISearchListingPageProps) {

        super(props);
        this._config = props.config;
        this._isMobileDevice = window.innerWidth <= this._config.mobileBreakpoint;
        this._initializing = true;

        // Initialize filter state store
        this._fullPageSize = this._isMobileDevice ? this._config.articleList.mobilePageSize : this._config.articleList.pageSize;
        this._store = new SearchListingFilterStore(this._config.apiEndpoint, this._fullPageSize);

        // Initialize ad service
        this._adService = new AdService();

        // Set initial component state
        this.state = {
            isLoading: (this._config.inEditMode === 'true') ? false : true,            
            mobileFiltersAreOpen: false,
            articleListAds: new Array<HTMLElement>(),
            selectedCategory: undefined,
            selectedBodyParts: new Array<number>(),
            selectedLanguage: ['en'],
            selectedAges: new Array<number>(),
            ...this._store.getInitialState()
        };

        // Bind methods
        this.bindMethods();

        // Configure debugging helpers
        window[this._config.globalPropName] = {}
        window[this._config.globalPropName].lastAction = undefined;
        window[this._config.globalPropName].currentState = this.state;
    }

    componentDidMount() {

        // Try to update state using last logged action unless new search
        const lastActionJson: string = window.sessionStorage.getItem(this._lastActionKey);
        if (!_.isNil(lastActionJson)) {

            const lastAction = JSON.parse(lastActionJson) as SLP.ISearchListingFilterAction;
            if (lastAction.searchString === this._config.searchString) {
                this.updateState(lastAction);
            } else this.updateState();

        } else this.updateState();

        // Set callback to render ads when api is ready
        if (this._config.inEditMode === 'false') {
            this._adService.setRenderCallback(() => this.renderAdBlocks());
        }
        
    }

    componentDidUpdate() {

        this.smoothScroll();
        this._adService.refreshAdBlocks();

    }

    get areFiltersApplied(): boolean {
        return this.selectedCategoryArray.length > 0 || this.state.selectedLanguage.length > 0 ||
            this.state.selectedBodyParts.length > 0 || this.state.selectedAges.length > 0;
    }

    get noResultMessage(): { __html: string } {
        return {
            __html: this._config.articleList.noResultMessage
        };
    }

    get selectedCategoryArray(): Array<string> {
        return _.isNil(this.state.selectedCategory) ? [] : [this.state.selectedCategory];
    }

    //get selectedLanguageArray(): Array<string> {
    //    return _.isNil(this.state.selectedLanguage) ? [] : [this.state.selectedLanguage];
    //}

    private bindMethods(): void {
        this.mobileClickHandler = this.mobileClickHandler.bind(this);
        this.mobileClearHandler = this.mobileClearHandler.bind(this);
        this.categoryChangeHandler = this.categoryChangeHandler.bind(this);
        this.bodyPartChangeHandler = this.bodyPartChangeHandler.bind(this);
        this.ageChangeHandler = this.ageChangeHandler.bind(this);
        this.languageChangeHandler = this.languageChangeHandler.bind(this);
        this.nextPageHandler = this.nextPageHandler.bind(this);
        this.prevPageHandler = this.prevPageHandler.bind(this);
        this.pageBtnHandler = this.pageBtnHandler.bind(this);
    }

    private updateState(params?: {}): void {

        // Short circuit if in edit mode or no search string is specified
        if (this._config.inEditMode === 'true' || (_.isNil(this._config.searchString) || this._config.searchString === '')) {
            if (this.state.isLoading) {
                this.setState({ isLoading: false });
            }
            return;
        }

        // Create action from current state and provided parameters
        const action = _.assign<SLP.ISearchListingFilterAction, {}>({
            page: this.state.page,
            searchString: this._config.searchString,
            trackingId: this.state.trackingId,
            category: this.state.selectedCategory,
            bodyParts: this.state.selectedBodyParts,
            language: this.state.selectedLanguage,
            ages: this.state.selectedAges
        }, params || {});

        window[this._config.globalPropName].lastAction = action;
        window.sessionStorage.setItem(this._lastActionKey, JSON.stringify(action));

        // Call store to get updated state
        this._store.getUpdatedState(action)
            .then((filterState) => {

                if (this._initializing)
                    this._initializing = false;

                this.setState({
                    isLoading: false,
                    selectedCategory: action.category,
                    selectedBodyParts: action.bodyParts,
                    selectedLanguage: action.language,
                    selectedAges: action.ages,
                    ...filterState
                }, () => window[this._config.globalPropName].currentState = this.state);

            }).catch((error) => console.error(error));

        // Update page to loading state
        this.setState({ isLoading: true });
    }

    private toggleArray<T>(array: Array<T>, value: T) {

        let newArray: Array<T>;
        if (array.indexOf(value) === -1) {
            newArray = array.concat(value);
        } else {
            newArray = array.filter(x => x !== value);
        }
        return newArray;

    }

    private renderAdBlocks(): void {
        // Append ad blocks to gutter
        this._adService.getAdBlocks('gutter').forEach(x => this._adGutter.appendChild(x));
        // Update state with article list ad blocks
        this.setState({ articleListAds: this._adService.getAdBlocks('articleList') }, () => {
           this._adService.refreshAdBlocks();
        });
    }

    public smoothScroll(): void {
        // set an interval to update scrollTop attribute every 25 ms
        var timer = setInterval(function () {
            var pixelJump = 40;

            if (document.documentElement.scrollTop != 0 || document.body.scrollTop != 0) {
                document.documentElement.scrollTop -= pixelJump;
                document.body.scrollTop -= pixelJump;
            }

            // end interval if the scroll is completed
            if (document.documentElement.scrollTop === 0 && document.body.scrollTop === 0) clearInterval(timer);
        }, 25);
    }

    public nextPageHandler(): void {
        // Call into store to page articles list
        if (this.state.page >= this.state.totalPages) return;

        const nextPage = this.state.page + 1;
        this.updateState({ page: nextPage });
    }

    public prevPageHandler(): void {
        // Call into store to page articles list
        if (this.state.page <= 1) return;

        const prevPage = this.state.page - 1;
        this.updateState({ page: prevPage });
    }

    public pageBtnHandler(page: number): void {
        // Call into store to page articles list
        this.updateState({ page: page });
    }

    public categoryChangeHandler(category: string): void {

        if (this.state.selectedCategory !== category) {
            this.updateState({ page: 1, category: category });
        } else this.updateState({ page: 1, category: undefined });

    }

    public bodyPartChangeHandler(bodyPart: number): void {

        const newBodyParts = this.toggleArray(this.state.selectedBodyParts, bodyPart);
        this.updateState({ page: 1, bodyParts: newBodyParts });

    }

    public languageChangeHandler(language: string): void {

        const newLang = this.toggleArray(this.state.selectedLanguage, language);
        // if (this.state.selectedLanguage !== language) {
        this.updateState({ page: 1, language: newLang });
       // } else this.updateState({ page: 1, language: undefined });

    }

    public ageChangeHandler(age: number) {

        const newAges = this.toggleArray(this.state.selectedAges, age);
        this.updateState({ page: 1, ages: newAges });

    }

    public mobileClickHandler(): void {

        if (this._isMobileDevice) {
            this.setState((prevState: SLP.ISearchListingPageState) => {
                return { mobileFiltersAreOpen: !prevState.mobileFiltersAreOpen }
            }, () => document.body.classList.toggle('filtersAreOpen'));
        }

    }

    public mobileClearHandler(): void {

        if (this.areFiltersApplied) {
            this.updateState({
                page: 1,
                category: undefined,
                bodyParts: new Array<number>(),
                language: new Array<string>(),
                ages: new Array<number>()
            });
        }

    }

    public getFilterCount(): number {
        let count: number = 0;
        count += (this.state.selectedBodyParts.length + this.state.selectedAges.length);
        if (!_.isNil(this.state.selectedCategory))
            count += 1;
        if (!_.isNil(this.state.selectedLanguage))
            count += 1;
        return count;
    }

    render() {
        return (
            <div>
                {!this._initializing &&
                    <SearchListingHeader resultCount={this.state.articles.length} searchString={this._config.searchString} />
                }
                <div className="listpage search-page">

                    {/* Filter Column */}
                    <div className={'listpage-col filters' + (this.state.mobileFiltersAreOpen ? ' filtersAreOpen' : '')}>

                        {/* Filter Column Header */}
                        <div className="filters-header" onClick={this.mobileClickHandler}>
                            <h6 className="filters-header-text">{this._config.filters.filterHeader}</h6>
                            {this._isMobileDevice && this.getFilterCount() > 0 &&
                                <small className="filters-header-count">
                                    {this.getFilterCount()}
                                </small>
                            }
                        </div>

                        {/* Filter Groups - Language, Category, BodyParts & Ages */}
                        <CheckboxFilterGroup
                            name="languages"
                            filterGroup={this._config.filters.languages}
                            appliedValues={this.state.selectedLanguage}
                            availableValues={this.state.availableLanguages}
                            preventDisable={true}
                            onChangeHandler={this.languageChangeHandler} />
                        <CheckboxFilterGroup
                            name="categories"
                            filterGroup={this._config.filters.categories}
                            appliedValues={this.selectedCategoryArray}
                            availableValues={this.state.availableCategories}
                            preventDisable={false}
                            onChangeHandler={this.categoryChangeHandler} />
                        <CheckboxFilterGroup
                            name="bodyParts"
                            filterGroup={this._config.filters.bodyParts}
                            appliedValues={this.state.selectedBodyParts}
                            availableValues={this.state.availableBodyParts}
                            preventDisable={false}
                            onChangeHandler={this.bodyPartChangeHandler} />
                        <CheckboxFilterGroup
                            name="ages"
                            filterGroup={this._config.filters.ages}
                            appliedValues={this.state.selectedAges}
                            availableValues={this.state.availableAges}
                            preventDisable={false}
                            onChangeHandler={this.ageChangeHandler} />                      

                        {/* Mobile Filter Dialog Controls */}
                        <div className="filters-section filters-controls">
                            <div className={'filters-controls-clear' + (this.areFiltersApplied ? ' canBeCleared' : '')} onClick={this.mobileClearHandler}>
                                {this._config.filters.controls.clearButtonText}
                            </div>
                            <div className="filters-controls-done btn-primary" onClick={this.mobileClickHandler}>
                                {
                                    (this.areFiltersApplied) ?
                                        this._config.filters.controls.applyButtonText : this._config.filters.controls.doneButtonText
                                }
                            </div>
                        </div>

                    </div>
                    {/* Article List Column */}
                    <div className="listpage-col list">

                        {/* Article List */}
                        {this.state.articles.length > 0 &&
                            <SearchArticleList
                                isLoading={this.state.isLoading}
                                searchString={this._config.searchString}
                                trackingId={this.state.trackingId}
                                articles={this.state.articles}
                                pageSize={this._fullPageSize}
                                adPosition={this._config.articleList.adPosition}
                                articleListAds={this.state.articleListAds} />
                        }

                        {/* No Results Message */}
                        {!this.state.isLoading && this.state.articles.length <= 0 &&
                            <div className="search-listing-noresults" dangerouslySetInnerHTML={this.noResultMessage}></div>
                        }

                        {/* Article List Pager */}
                        {this.state.articles.length > 0 &&
                            <ArticleListPager
                                totalPages={this.state.totalPages}
                                currentPage={this.state.page}
                                prevBtnHandler={this.prevPageHandler}
                                nextBtnHandler={this.nextPageHandler}
                                pageBtnHander={this.pageBtnHandler} />
                        }
                    </div>
                    <div className="listpage-col gutter" ref={(x) => this._adGutter = x}></div>
                </div>
            </div>
        );
    }

}



// WEBPACK FOOTER //
// ./~/source-map-loader!./app/src/slp/search-listing-page.tsx